/**
 * @fileoverview Provides a utility function for creating directories with safety checks and validation. Ensures directories are created within the current working directory and handles both absolute and relative paths using recursive directory creation.
 */

import { ToolDefinition } from '@iterator/shared-node/ai/llm_types'
import * as fs from 'fs'
import * as path from 'path'
import { z } from 'zod'

export const MakeDirectoryArgs = z.object({
  dirPath: z.string(),
})
export type MakeDirectoryArgs = z.infer<typeof MakeDirectoryArgs>
export type MakeDirectoryCall = { name: 'makeDirectory'; args: MakeDirectoryArgs }

export function makeDirectory(dirPath: string): string {
  if (typeof dirPath !== 'string' || dirPath.trim() === '') {
    throw new Error('Invalid dirPath: must be a non-empty string')
  }

  const absoluteDirPath = path.isAbsolute(dirPath) ? dirPath : path.join(process.cwd(), dirPath)
  if (!absoluteDirPath.startsWith(process.cwd())) {
    throw new Error(
      'Invalid dirPath: must be within the current working directory or its subdirectories',
    )
  }

  if (!fs.existsSync(absoluteDirPath)) {
    fs.mkdirSync(absoluteDirPath, { recursive: true })
  }

  return `Created directory at ${dirPath}`
}

export const makeDirectoryTool: ToolDefinition = {
  name: 'makeDirectory',
  function: {
    name: 'makeDirectory',
    description: 'Creates a directory at the specified path',
    parameters: {
      type: 'object',
      properties: {
        dirPath: {
          type: 'string',
          description: 'The path to the directory',
        },
      },
      required: ['dirPath'],
    },
  },
  execute: async (args: unknown): Promise<string> => {
    const { dirPath } = MakeDirectoryArgs.parse(args)
    return makeDirectory(dirPath)
  },
}
